import { CircleNotchIcon } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminUserSummary, apiErrorDetail, apiErrorStatus, useSetUserStatus } from "@/lib/api";
import { logger } from "@/lib/logger";

interface UserStatusDialogProps {
  /** The user to deactivate when active, or to reactivate when deactivated. */
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Confirm step for deactivating or reactivating a user. */
export function UserStatusDialog({ user, open, onOpenChange }: UserStatusDialogProps) {
  const statusMutation = useSetUserStatus();
  const [error, setError] = useState("");
  const deactivate = !user?.disabled_at;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setError("");
    }
    onOpenChange(nextOpen);
  };

  const handleConfirm = () => {
    if (!user) {
      return;
    }
    setError("");
    logger.debug("[admin]", deactivate ? "deactivating" : "reactivating", user.id);
    statusMutation.mutate(
      { userId: user.id, disabled: deactivate },
      {
        onSuccess: () => handleOpenChange(false),
        onError: (err) => {
          const status = apiErrorStatus(err);
          const detail = apiErrorDetail(err);
          logger.debug("[admin] set user status failed:", status, detail);
          setError(statusErrorMessage(status, detail));
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{deactivate ? "Deactivate user" : "Reactivate user"}</DialogTitle>
          <DialogDescription>
            {deactivate ? (
              <>
                Deactivate <strong>{user?.email}</strong>? They are signed out everywhere at once
                and can&apos;t sign in until reactivated.
              </>
            ) : (
              <>
                Reactivate <strong>{user?.email}</strong>? They can sign in again; old sessions stay
                ended.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={deactivate ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={statusMutation.isPending}
          >
            {statusMutation.isPending ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            ) : deactivate ? (
              "Deactivate"
            ) : (
              "Reactivate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function statusErrorMessage(status: number | undefined, detail: string | undefined): string {
  if (status === 400 && detail === "cannot_deactivate_self") {
    return "You can't change the status of your own account.";
  }
  if (status === 403) {
    return "Only an active admin can change a user's status.";
  }
  if (status === 404) {
    return "This user no longer exists. Refresh the list.";
  }
  return "Failed to change the user's status. Please try again.";
}
